import { prefersReducedMotion } from './motion';

/**
 * Tree of Life reveal animation. The tree is a single piece of filled artwork,
 * so instead of drawing individual strokes we uncover it with a soft vertical
 * wipe that climbs from the roots at the bottom to the canopy at the top — the
 * same root-to-canopy direction the old stroke-drawn tree grew in.
 *
 * The hero tree reveals once on load. The placenta tree reveals when it scrolls
 * into view, so the visitor feels they grow it while reading the three steps.
 *
 * Without JavaScript, or under reduced motion, the SVG renders fully revealed
 * (handled in CSS); this module never hides anything in those cases.
 */

const REVEAL_DURATION = 2600;
const REVEAL_EASING = 'cubic-bezier(0.22, 0.61, 0.24, 1)';

// Fully clipped from the bottom up: nothing shows until we reveal.
const CLIPPED = 'inset(100% 0 0 0)';
const REVEALED = 'inset(0 0 0 0)';

export function initTree(): void {
  const trees = Array.from(document.querySelectorAll<HTMLElement>('[data-tree]'));
  if (trees.length === 0) return;

  // Under reduced motion the CSS already shows a complete, static tree.
  if (prefersReducedMotion()) return;

  const prepared = trees
    .map((wrapper) => prepareTree(wrapper))
    .filter((t): t is { wrapper: HTMLElement; svg: SVGSVGElement } => t !== null);

  prepared.forEach(({ wrapper, svg }) => {
    if (wrapper.dataset.tree === 'placenta') {
      observeIntoView(wrapper, svg);
    } else {
      // Hero: reveal shortly after load once layout has settled.
      window.requestAnimationFrame(() => window.setTimeout(() => drawTree(svg), 180));
    }
  });
}

function prepareTree(
  wrapper: HTMLElement
): { wrapper: HTMLElement; svg: SVGSVGElement } | null {
  const svg = wrapper.querySelector<SVGSVGElement>('svg.tree-animated');
  if (!svg) return null;

  // Hidden starting state, no transition yet.
  svg.style.clipPath = CLIPPED;
  svg.style.transition = 'none';

  // Force layout so the hidden state is committed before we enable transitions.
  void svg.getBoundingClientRect();

  return { wrapper, svg };
}

function drawTree(svg: SVGSVGElement): void {
  if (svg.classList.contains('is-drawn')) return;
  svg.classList.add('is-drawn');

  svg.style.transition = `clip-path ${REVEAL_DURATION}ms ${REVEAL_EASING}`;
  svg.style.clipPath = REVEALED;
}

// Observe the wrapper, not the SVG: the clip-path that hides the SVG collapses
// its intersection area to zero, so a threshold on the SVG itself would never
// be crossed. The wrapper is never clipped, so its ratio is honest.
function observeIntoView(wrapper: HTMLElement, svg: SVGSVGElement): void {
  if (!('IntersectionObserver' in window)) {
    drawTree(svg);
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          drawTree(svg);
          obs.disconnect();
        }
      });
    },
    { threshold: 0.35 }
  );

  observer.observe(wrapper);
}
